import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import classNames from "classnames/bind";
import ContentLoader from "react-content-loader";
import dashboardModel from "models/dashboard";
import {
  ApplicationListOverview,
  BlogPanel,
  DashboardMessageBanner,
  ErrorNoticeBanner,
  Layout,
  Panel,
  SearchPanel,
} from "components";
import { ZebraTable } from "tables";
import styles from "styles/modules/dashboard.module.scss";

const cx = classNames.bind(styles);

export default function Dashboard(props) {
  const [data, setData] = useState({ props });
  const [hasLoaded, setHasLoaded] = useState();
  const { data: session } = useSession();
  const bannerNotice = useRef();
  const router = useRouter();

  const { error_message, message } = router.query;

  const setDataFromDashboardModel = async () => {
    setData(await dashboardModel(session));
    setHasLoaded(true);
  };

  useEffect(() => {
    if (session) {
      setDataFromDashboardModel();
    }
  }, [session]);

  const {
    props: { applications, applicationSummary, blogData, base, errors } = {},
  } = data;

  let topRowStyle = blogData
    ? cx("topRowWithBlogPanel")
    : cx("topRowWithoutBlogPanel");

  return (
    <Layout base={base}>
      {!session ? (
        "Loading"
      ) : (
        <>
          <ErrorNoticeBanner errors={errors} ref={bannerNotice} />
          {(error_message || message) &&
            DashboardMessageBanner({ error_message, message })}
          <div className={topRowStyle}>
            <div className={cx("panelWrapper")}>
              {hasLoaded && applicationSummary ? (
                <ApplicationListOverview
                  applicationSummary={applicationSummary}
                />
              ) : (
                <Panel size="small">
                  <ContentLoader
                    uniqueKey="applicationSummary"
                    viewBox="0 0 527 232"
                  >
                    <rect x="0" y="0" rx="3" ry="3" width="115" height="25" />
                    <rect x="0" y="55" rx="3" ry="3" width="120" height="14" />
                    <rect x="0" y="93" rx="3" ry="3" width="65" height="14" />
                    <rect x="0" y="132" rx="3" ry="3" width="130" height="14" />
                    <rect x="0" y="171" rx="3" ry="3" width="110" height="14" />
                    <rect x="0" y="210" rx="3" ry="3" width="70" height="14" />
                    <circle cx="509" cy="15" r="14" />
                  </ContentLoader>
                </Panel>
              )}
            </div>
            <div className={cx("panelWrapper")}>
              {hasLoaded && !errors ? (
                <SearchPanel />
              ) : (
                <Panel size="small">
                  <ContentLoader uniqueKey="searchPanel" viewBox="0 0 527 232">
                    <rect x="0" y="0" rx="3" ry="3" width="60" height="25" />
                    <rect x="0" y="55" rx="3" ry="3" width="320" height="14" />
                    <rect x="0" y="90" rx="3" ry="3" width="100%" height="45" />
                    <rect x="0" y="150" rx="3" ry="3" width="75%" height="14" />
                    <rect x="0" y="190" rx="3" ry="3" width="100" height="55" />
                  </ContentLoader>
                </Panel>
              )}
            </div>
            <div className={cx("panelWrapper")}>
              {blogData && <BlogPanel {...blogData} />}
            </div>
          </div>
          {applications?.length > 0 && (
            <Panel size="small">
              <div className={cx("applicationsList")} id="applicationsList">
                <h3 className={cx("title")}>
                  Applications that need your attention
                </h3>
                <p className={cx("description")}>
                  You can continue, amend or complete the below applications.
                </p>
                <ZebraTable
                  configuration="dashboard"
                  tableData={applications}
                />
              </div>
            </Panel>
          )}
          {(!hasLoaded || errors) && (
            <Panel size="small">
              <ContentLoader uniqueKey="applicationList" viewBox="0 0 1124 332">
                <rect x="0" y="0" rx="3" ry="3" width="330" height="25" />
                <rect x="0" y="50" rx="3" ry="3" width="420" height="14" />
                <rect x="0" y="90" rx="3" ry="3" width="100%" height="40" />
                <rect
                  x="90%"
                  y="140"
                  rx="3"
                  ry="3"
                  width="9%"
                  height="30"
                ></rect>
                <rect x="0" y="180" rx="3" ry="3" width="100%" height="1" />
                <rect
                  x="90%"
                  y="190"
                  rx="3"
                  ry="3"
                  width="9%"
                  height="30"
                ></rect>
                <rect x="0" y="230" rx="3" ry="3" width="100%" height="1" />
                <rect
                  x="90%"
                  y="240"
                  rx="3"
                  ry="3"
                  width="9%"
                  height="30"
                ></rect>
                <rect x="0" y="280" rx="3" ry="3" width="100%" height="1" />
                <rect
                  x="90%"
                  y="290"
                  rx="3"
                  ry="3"
                  width="9%"
                  height="30"
                ></rect>
                <rect x="0" y="330" rx="3" ry="3" width="100%" height="1" />
              </ContentLoader>
            </Panel>
          )}
        </>
      )}
    </Layout>
  );
}

export async function getStaticProps() {
  return await dashboardModel();
}
